"use client";

import React from "react";

interface ErrorBoundaryProps {
  developmentMode?: boolean;
  fallbackMode?: "overlay" | "fullscreen";
}

interface ErrorBoundaryState {
  hasError: boolean;
  errors: Array<{
    message: string;
    timestamp: string;
    type?: string;
  }>;
  showOverlay: boolean;
}

export class ErrorBoundary extends React.Component<
  React.PropsWithChildren<ErrorBoundaryProps>,
  ErrorBoundaryState
> {
  private errorCollectionTimeout: NodeJS.Timeout | null = null;

  constructor(props: React.PropsWithChildren<ErrorBoundaryProps>) {
    super(props);
    this.state = {
      hasError: false,
      errors: [],
      showOverlay: false,
    };
  }

  componentDidMount() {
    // Listen for unhandled promise rejections
    window.addEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );

    // Listen for global errors
    window.addEventListener("error", this.handleGlobalError);
  }

  componentWillUnmount() {
    window.removeEventListener(
      "unhandledrejection",
      this.handleUnhandledRejection
    );
    window.removeEventListener("error", this.handleGlobalError);

    if (this.errorCollectionTimeout) {
      clearTimeout(this.errorCollectionTimeout);
    }
  }

  addError = (error: { message: string; timestamp: string; type?: string }) => {
    const isDevelopmentMode =
      this.props.developmentMode ?? process.env.NODE_ENV === "development";
    const fallbackMode = this.props.fallbackMode ?? "fullscreen";

    this.setState((prevState) => ({
      hasError: true,
      errors: [...prevState.errors, error],
      showOverlay: isDevelopmentMode && fallbackMode === "overlay",
    }));

    // In development mode with overlay, collect errors for a short period
    if (isDevelopmentMode && fallbackMode === "overlay") {
      if (this.errorCollectionTimeout) {
        clearTimeout(this.errorCollectionTimeout);
      }

      // Show overlay after collecting errors for 1 second
      this.errorCollectionTimeout = setTimeout(() => {
        this.setState({ showOverlay: true });
      }, 1000);
    }
  };

  handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    console.log("🔴 Unhandled Promise Rejection:", event.reason);

    const errorMessage =
      event.reason instanceof Error
        ? event.reason.message
        : String(event.reason);

    const errorData = {
      error: errorMessage,
      stack: event.reason instanceof Error ? event.reason.stack : undefined,
      timestamp: new Date().toISOString(),
      type: "unhandledrejection",
    };

    // Send to parent window if in iframe
    if (window.parent && window.parent !== window) {
      try {
        window.parent.postMessage(
          {
            type: "IFRAME_ERROR",
            error: errorData,
          },
          "*"
        );
      } catch (e) {
        console.warn("Failed to send error to parent:", e);
      }
    }

    this.addError({
      message: `Promise Rejection: ${errorMessage}`,
      timestamp: errorData.timestamp,
      type: "promise",
    });

    // Prevent the default browser behavior
    event.preventDefault();
  };

  handleGlobalError = (event: ErrorEvent) => {
    console.log("🔴 Global Error:", event.error);

    const errorData = {
      error: event.message || "Unknown global error",
      stack: event.error?.stack,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      timestamp: new Date().toISOString(),
      type: "globalerror",
    };

    // Send to parent window if in iframe
    if (window.parent && window.parent !== window) {
      try {
        window.parent.postMessage(
          {
            type: "IFRAME_ERROR",
            error: errorData,
          },
          "*"
        );
      } catch (e) {
        console.warn("Failed to send error to parent:", e);
      }
    }

    this.addError({
      message: event.message || "Unknown global error",
      timestamp: errorData.timestamp,
      type: "global",
    });
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    console.log("🔴 React Error Boundary caught error:", error);
    // Only set hasError to true, let componentDidCatch handle adding the error
    return {
      hasError: true,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const errorData = {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    };

    // Console log (existing functionality)
    console.log("🔴 Error Boundary Details:", errorData);

    // Send to parent window if in iframe
    if (window.parent && window.parent !== window) {
      try {
        window.parent.postMessage(
          {
            type: "IFRAME_ERROR",
            error: errorData,
          },
          "*"
        );
      } catch (e) {
        console.warn("Failed to send error to parent:", e);
      }
    }

    // Use addError to properly handle the error collection
    this.addError({
      message: error.message,
      timestamp: errorData.timestamp,
      type: "react",
    });
  }

  renderErrorList = () => (
    <ul className="space-y-2 max-h-60 overflow-y-auto">
      {this.state.errors.map((error, index) => (
        <li
          key={index}
          className="flex items-start space-x-3 bg-red-50 border border-red-200 rounded-lg p-4"
        >
          <span className="text-red-500 font-bold text-lg flex-shrink-0">
            •
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              {error.type && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                  {error.type}
                </span>
              )}
              <span className="text-red-600 text-xs">
                {new Date(error.timestamp).toLocaleTimeString()}
              </span>
            </div>
            <p className="text-red-800 font-medium break-words">
              {error.message}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );

  render() {
    const isDevelopmentMode =
      this.props.developmentMode ?? process.env.NODE_ENV === "development";
    const fallbackMode = this.props.fallbackMode ?? "fullscreen";

    // Overlay mode: show errors as floating overlay while keeping app functional
    if (
      this.state.hasError &&
      isDevelopmentMode &&
      fallbackMode === "overlay"
    ) {
      return (
        <>
          {this.props.children}
          {this.state.showOverlay && (
            <div className="fixed inset-0 z-50 pointer-events-none">
              <div className="absolute top-4 right-4 max-w-md pointer-events-auto">
                <div className="bg-white shadow-2xl rounded-lg border-2 border-red-300">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-lg font-semibold text-red-800">
                        Development Errors
                      </h2>
                      <button
                        onClick={() => this.setState({ showOverlay: false })}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    </div>
                    {this.renderErrorList()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      );
    }

    // Fullscreen mode: traditional error boundary behavior
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-8">
          <div className="max-w-2xl w-full">
            <h2 className="text-2xl font-semibold mb-4 text-center text-red-800">
              Application Errors
            </h2>
            {this.renderErrorList()}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
